// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Budget Model v2 (ADR-052): thin, locale-bound accessors over THE canonical
 * glossary module in @bitebalance/i18n. Every metric NAME, definition and
 * status word on a web surface comes through here — never retyped in a
 * component, never re-authored in a view dictionary.
 */

import {
  GLOSSARY_KIND_LABEL,
  GLOSSARY_STATUS,
  GLOSSARY_UI,
  glossaryText,
  type GlossaryKind,
  type GlossaryLocale,
  type GlossaryTermId,
  type GlossaryTermText,
} from "@bitebalance/i18n"
import { interpolate } from "@bitebalance/i18n"
import { formatEurFromCents } from "@/i18n/format"

export type GoalStatus = "at_or_below_goal" | "above_goal" | "unavailable"
export type CheckoutStatus = "no_max" | "within_max" | "exceeds_max" | "unverifiable"

export function glossaryTerm(id: GlossaryTermId, locale: GlossaryLocale): GlossaryTermText {
  return glossaryText(id, locale)
}

export function glossaryKindLabel(kind: GlossaryKind, locale: GlossaryLocale): string {
  return GLOSSARY_KIND_LABEL[kind][locale]
}

export function glossaryUi(locale: GlossaryLocale) {
  return GLOSSARY_UI[locale]
}

export function goalStatusLabel(status: GoalStatus, locale: GlossaryLocale): string {
  return GLOSSARY_STATUS[locale].goal[status]
}

export function checkoutStatusLabel(status: CheckoutStatus, locale: GlossaryLocale): string {
  return GLOSSARY_STATUS[locale].checkout[status]
}

/**
 * The EXACT signed goal delta as words: "€0.01 above goal" / "€3.80 below
 * goal" / "Exactly at goal". Owner decision D-3: no tolerance band — one
 * cent above the goal is above the goal; the number is always shown.
 */
export function goalDeltaLabel(deltaCents: number, locale: GlossaryLocale): string {
  const s = GLOSSARY_STATUS[locale]
  if (deltaCents === 0) return s.atGoal
  const amount = formatEurFromCents(Math.abs(deltaCents), locale)
  return interpolate(deltaCents > 0 ? s.aboveGoalBy : s.belowGoalBy, { amount })
}

/** "€6.40 over maximum" / "€12.00 under maximum" (exact, signed). */
export function checkoutDeltaLabel(deltaCents: number, locale: GlossaryLocale): string {
  const s = GLOSSARY_STATUS[locale]
  const amount = formatEurFromCents(Math.abs(deltaCents), locale)
  return interpolate(deltaCents > 0 ? s.overMaxBy : s.underMaxBy, { amount })
}

/**
 * The full goal status phrase used on Today / Shop / Settings: the canonical
 * status word plus the exact delta, e.g. "Above goal · €0.01 above goal".
 * Falls back to the status word alone when the delta is unknown.
 */
export function goalStatusWithDelta(
  status: GoalStatus,
  deltaCents: number | null,
  locale: GlossaryLocale
): string {
  const word = goalStatusLabel(status, locale)
  if (status === "unavailable" || deltaCents === null) return word
  return `${word} · ${goalDeltaLabel(deltaCents, locale)}`
}
